import { Loader2 } from 'lucide-react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useCustomDomainSite } from '@/contexts/CustomDomainSiteContext';
import { lazy } from '@/lib/lazy-route';
import { Suspense } from 'react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

const LyrraOnePublicSitePage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicSitePage'));
const LyrraOnePublicSiteBlogPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicSiteBlogPage'));
const LyrraOnePublicSiteBlogPostPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicSiteBlogPostPage'));
const LyrraOneCreatorStorePage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneCreatorStorePage'));
const LyrraOneCreatorStoreFormationPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneCreatorStoreFormationPage'));
const LyrraOneCreatorStoreProductsPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneCreatorStoreProductsPage'));
const LyrraOneCreatorStoreProductPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneCreatorStoreProductPage'));
const LyrraOnePublicBundlePage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicBundlePage'));
const LyrraOnePublicAffiliatePage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicAffiliatePage'));
const LyrraOnePublicAffiliateDashboardPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicAffiliateDashboardPage'));
const LyrraOnePublicContactPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicContactPage'));
const LyrraOnePublicCommunitiesPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicCommunitiesPage'));
const LyrraOnePublicLegalPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicLegalPage'));
const LoSiteAuthPage = lazy(() => import('@/pages/lyrraone-marketing/public/LoSiteAuthPage'));

const APP_ROUTE_PREFIXES = [
  '/forgot-password',
  '/reset-password',
  '/auth',
  '/dashboard',
  '/marketing',
  '/learner',
  '/admin',
  '/settings',
  '/manager',
  '/my-training',
  '/community',
  '/f/',
  '/c/',
  '/demo/',
  '/deposit/',
  '/store',
  '/book/',
  '/eduflow/',
  '/podcasts/',
  '/api',
];

/** Pages légales plateforme uniquement (hors site formateur). */
const PLATFORM_ONLY_LEGAL_PATHS = new Set([
  '/accessibility',
]);

/** Pages légales du site formateur (marque blanche / domaine custom). */
const TRAINER_LEGAL_PATHS = new Set([
  '/cookies',
  '/privacy',
  '/terms',
  '/cgv',
  '/confidentialite',
]);

function SuspenseWrap({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="flex min-h-[40vh] items-center justify-center"><LoadingSpinner /></div>}>
      {children}
    </Suspense>
  );
}

function CustomDomainNotFound() {
  return (
    <div className="lyrraone-landing flex min-h-screen flex-col items-center justify-center gap-4 px-4">
      <p className="lo-marketplace-status">Page introuvable.</p>
      <Link to="/" className="lo-btn lo-btn-secondary">Retour à l&apos;accueil</Link>
    </div>
  );
}

/** Sur domaine custom vérifié, sert le site créateur à la racine (/ , /blog, /page). */
export function CustomDomainSiteGate() {
  const { loading, isCustomDomain, siteSlug } = useCustomDomainSite();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-hidden />
      </div>
    );
  }

  if (!isCustomDomain || !siteSlug) {
    return <Outlet />;
  }

  const pathNorm = pathname.replace(/\/$/, '') || '/';

  // Auth apprenant marque blanche : thème du site formateur (pas le login LyrraOne).
  if (pathNorm === '/login') {
    return (
      <SuspenseWrap>
        <LoSiteAuthPage mode="login" />
      </SuspenseWrap>
    );
  }
  if (pathNorm === '/register') {
    return (
      <SuspenseWrap>
        <LoSiteAuthPage mode="register" />
      </SuspenseWrap>
    );
  }

  if (APP_ROUTE_PREFIXES.some((p) => pathname.startsWith(p))) {
    return <Outlet />;
  }

  if (PLATFORM_ONLY_LEGAL_PATHS.has(pathNorm)) {
    return <Outlet />;
  }

  if (TRAINER_LEGAL_PATHS.has(pathNorm)) {
    return (
      <SuspenseWrap>
        <LyrraOnePublicLegalPage />
      </SuspenseWrap>
    );
  }

  const blogPostMatch = pathname.match(/^\/blog\/([^/]+)\/?$/);
  if (blogPostMatch) {
    return (
      <SuspenseWrap>
        <LyrraOnePublicSiteBlogPostPage />
      </SuspenseWrap>
    );
  }

  if (pathname === '/blog' || pathname === '/blog/') {
    return (
      <SuspenseWrap>
        <LyrraOnePublicSiteBlogPage />
      </SuspenseWrap>
    );
  }

  if (pathname === '/formations' || pathname === '/formations/') {
    return (
      <SuspenseWrap>
        <LyrraOneCreatorStorePage />
      </SuspenseWrap>
    );
  }

  if (pathname === '/boutique' || pathname === '/boutique/') {
    return (
      <SuspenseWrap>
        <LyrraOneCreatorStoreProductsPage />
      </SuspenseWrap>
    );
  }

  const productMatch = pathname.match(/^\/boutique\/([^/]+)\/?$/);
  if (productMatch) {
    return (
      <SuspenseWrap>
        <LyrraOneCreatorStoreProductPage />
      </SuspenseWrap>
    );
  }

  if (pathname === '/affiliate/dashboard' || pathname === '/affiliate/dashboard/') {
    return (
      <SuspenseWrap>
        <LyrraOnePublicAffiliateDashboardPage />
      </SuspenseWrap>
    );
  }

  if (pathname === '/affiliate' || pathname === '/affiliate/') {
    return (
      <SuspenseWrap>
        <LyrraOnePublicAffiliatePage />
      </SuspenseWrap>
    );
  }

  if (pathname === '/contact' || pathname === '/contact/') {
    return (
      <SuspenseWrap>
        <LyrraOnePublicContactPage />
      </SuspenseWrap>
    );
  }

  if (pathname === '/communautes' || pathname === '/communautes/') {
    return (
      <SuspenseWrap>
        <LyrraOnePublicCommunitiesPage />
      </SuspenseWrap>
    );
  }

  const formationMatch = pathname.match(/^\/formations\/(?!packs\/)([^/]+)\/?$/);
  if (formationMatch) {
    return (
      <SuspenseWrap>
        <LyrraOneCreatorStoreFormationPage />
      </SuspenseWrap>
    );
  }

  const bundleMatch = pathname.match(/^\/formations\/packs\/([^/]+)\/?$/);
  if (bundleMatch) {
    return (
      <SuspenseWrap>
        <LyrraOnePublicBundlePage />
      </SuspenseWrap>
    );
  }

  if (pathname === '/' || pathname === '') {
    return (
      <SuspenseWrap>
        <LyrraOnePublicSitePage />
      </SuspenseWrap>
    );
  }

  const pageMatch = pathname.match(/^\/([^/]+)\/?$/);
  if (
    pageMatch
    && pageMatch[1] !== 'blog'
    && pageMatch[1] !== 'formations'
    && pageMatch[1] !== 'boutique'
    && pageMatch[1] !== 'affiliate'
    && pageMatch[1] !== 'contact'
    && pageMatch[1] !== 'communautes'
    && !PLATFORM_ONLY_LEGAL_PATHS.has(`/${pageMatch[1]}`)
    && !TRAINER_LEGAL_PATHS.has(`/${pageMatch[1]}`)
  ) {
    return (
      <SuspenseWrap>
        <LyrraOnePublicSitePage />
      </SuspenseWrap>
    );
  }

  return <CustomDomainNotFound />;
}
